<template>
  <layout hide-search-cta>
    <div class="Booking">
      <div v-if="hasHeader" class="Layout Booking-header">
        <ai-separator :margin="2" />
      </div>

      <div class="Layout Layout--context">
        <booking-context />
      </div>

      <div class="Layout--grey Layout-wrapper">
        <div class="Layout">
          <div class="Layout-section Booking-body">
            <grid-item sm="4" md="12" class="Booking-timeValidationBanner">
              <template v-if="invalidRoomsError" />
              <ai-error-tool-tip v-else-if="error" />

              <template v-else>
                <booking-time-validation-tooltip />
                &nbsp;
                <booking-child-as-adult-tooltip
                  v-if="bookingStore.childAsAdult" />
              </template>
            </grid-item>
            <grid-item sm="4" md="12" l="4">
              <client-only>
                <booking-recap
                  v-if="bookingReady"
                  :all-rooms-valid="roomValidated"
                  @promo-code-applied="handleRerender" />
              </client-only>
            </grid-item>

            <grid-item sm="4" md="12" l="8" class="Booking-mainContent">
              <booking-invalid-rooms v-if="invalidRoomsError" />

              <template v-else>
                <booking-stepper />
                <client-only>
                  <NuxtPage
                    v-if="bookingReady"
                    :key="nuxtPageKey"
                    v-model:room-error="invalidRoomsError"
                    @want-signup="onWantSignup"
                    @submit="submit"
                    @error="err => ((roomValidated = false), (error = err))"
                    @data-valid="() => (roomValidated = true)" />
                </client-only>
              </template>
            </grid-item>

            <grid-item sm="4" md="12" l="12">
              <booking-help-center />

              <ai-legal-mentions-price-legal-mention
                terms-type="priceAndCurrenciesLegalMention"
                :personal-data="personalData"
                :lang="localLanguage" />
            </grid-item>
          </div>
        </div>
      </div>

      <client-only>
        <booking-refresh-prices />
      </client-only>
    </div>
  </layout>
</template>

<script setup lang="ts">
import { ApolloError } from '@apollo/client/errors';
import { GraphQLError } from 'graphql';
import { onBeforeRouteLeave } from 'vue-router';

import {
  useBookingPriceStaleTimer,
  useBookingQueryParametersContext,
  useBookingStore,
} from '~~/domains/booking';
import { useBookingBasket } from '~~/domains/booking/hooks/useBookingBasket';
import {
  useBookingSeoAsyncData,
  useHotelContextAsyncData,
} from '~~/domains/graphql';
import { useUserStore } from '~~/domains/user';
import { useLayoutStore } from '~~/domains/ux';
import { event } from '~~/helpers/gtm.helper';
import { isLaunchedInFrame } from '~~/helpers/iframe-context.helper';

type BookingRouteName = 'room' | 'rate' | 'details' | 'payment' | 'credit-card';

useBookingQueryParametersContext();

useHead({
  meta: [{ name: 'robots', content: 'noindex' }],
});

definePageMeta({
  layout: 'default-not-inversed',
});

const defaultPath = '/booking';
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: defaultPath,
    fr: defaultPath,
    de: defaultPath,
    ru: defaultPath,
    tr: defaultPath,
  },
});

const bookingStore = useBookingStore();
const layoutStore = useLayoutStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const { locale, t } = useI18n();
const nuxtPageKey = ref(0);
const { currentStep } = useCurrentBookingStep();

const { data: hotelContext } = await useHotelContextAsyncData({
  hotelId: (route.params.ridcode as string) ?? bookingStore.hotelId,
});

watch(
  hotelContext,
  context => {
    if (!context) return;

    if (context.hotel?.maxChildAge) {
      bookingStore.maxChildrenAge = context.hotel.maxChildAge;
    }

    bookingStore.hotelDetails.city = context.hotel?.address?.city ?? '';
    bookingStore.hotelDetails.cityCode = context.hotel?.address?.zipCode ?? ' ';
    bookingStore.hotelDetails.country = context.hotel?.address?.country ?? '';
    bookingStore.hotelDetails.countryCode =
      context.hotel?.address?.countryCode ?? '';
    bookingStore.hotelDetails.hotelName = context.hotel?.name ?? '';
    bookingStore.hotelDetails.brandCode = context.hotel?.brand ?? '';

    return context;
  },
  {
    immediate: true,
    deep: true,
  },
);

const { createBasket, updateBasket, init } = useBookingBasket();
const { data } = await useBookingSeoAsyncData();

const onWantSignup = (value: boolean) => {
  bookingStore.wantSignup = value;
};

const { unregisterScrollToTop } = useScrollToTop(['page:finish']);
const { refreshStalePriceTimer, onShouldRedirect } =
  useBookingPriceStaleTimer();

const error = ref<ApolloError>();
const invalidRoomsError = ref(false);
const queryParametersListener = ref<() => void>();
const roomValidated = ref<Boolean>(false);

router.beforeEach(() => {
  error.value = undefined;
  layoutStore.pauseSkeletons = false;
});

const hasIdentificationCookies = useHasIdentificationCookies();
const hasLoadedUser = computed(() => userStore.hasLoadedUser);
const bookingReady = computed(
  () => hasIdentificationCookies.value && hasLoadedUser.value,
);
const metadata = computed(() => {
  if (!data.value?.bookingSeo || !(currentStep.value in data.value.bookingSeo))
    return;

  return data.value.bookingSeo[currentStep.value];
});

const fallback = computed(() => {
  switch (currentStep.value) {
    case 'room':
      return { title: t('seo.bookingRoom.title') };

    case 'rate':
      return { title: t('seo.bookingRate.title') };

    case 'details':
      return { title: t('seo.bookingDetails.title') };

    case 'payment':
      return { title: t('seo.bookingPayment.title') };
  }
});
useSeoMetadata({ metadata, fallback });

const personalData = computed(
  () => currentStep.value === 'details' || currentStep.value === 'payment',
);

const localLanguage = ref(locale.value ?? 'en');

const hasHeader = computed(() => {
  return !isLaunchedInFrame();
});

const routeMapping = computed(() => {
  return new Map<BookingRouteName, string | null | (() => string)>([
    ['room', 'booking-ridcode-rate'],
    ['rate', redirectToStep3],
    ['details', redirectToStep4],
    ['payment', null],
  ]);
});

onMounted(async () => {
  const preferentialCode = usePreferentialCode();

  if (preferentialCode) {
    router.push({
      query: {
        ...router.currentRoute.value.query,
        preferentialCode,
      },
      replace: true,
    });
  }

  if (route.query.error && route.query.error === 'invalid-rooms') {
    invalidRoomsError.value = true;
  }

  refreshStalePriceTimer();

  const bookingRoutePath = localePath({ name: 'booking' });

  if (router.currentRoute.value.fullPath === bookingRoutePath) {
    router.replace({
      path: localePath({
        ...router.currentRoute.value,
        name: 'booking-ridcode-room',
        params: {
          ...router.currentRoute.value.params,
          ridcode: bookingStore.hotelId as string,
        },
      }),
    });
  }

  await init();
});

onBeforeRouteLeave(() => {
  unregisterScrollToTop();

  if (typeof queryParametersListener.value === 'function') {
    queryParametersListener.value();
  }
});

const redirectToStep3 = () => {
  bookingStore.commitRoom();

  if (bookingStore.areAllRoomsConfigured) {
    try {
      error.value = undefined;

      // Since we come in step 1, we are in a new basket
      // Delete booking keys
      bookingStore.beneficiary = null;
      bookingStore.additionalInformation = '';
      bookingStore.currentBasket = null;
      bookingStore.basketId = null;
      bookingStore.asAnonymous = !userStore.isLogged;

      createBasket();

      return 'booking-details';
    } catch (e) {
      error.value = e as ApolloError;

      return '';
    }
  } else {
    return 'booking-ridcode-room';
  }
};

const redirectToStep4 = () => {
  try {
    error.value = undefined;
    updateBasket();

    return 'booking-payment';
  } catch (e) {
    error.value = e as ApolloError;

    return '';
  }
};

async function submit(step: BookingRouteName) {
  const nextRoute: any = routeMapping.value.get(step);
  if (!nextRoute) {
    return;
  }

  const preferentialCode = usePreferentialCode();

  if (preferentialCode) {
    bookingStore.preferentialCode = preferentialCode;
  }

  if (step === 'rate') {
    refreshStalePriceTimer();
  }

  const name: any =
    typeof nextRoute === 'string' ? nextRoute : await nextRoute();
  if (!name) {
    return;
  }

  const params: any = { ridcode: bookingStore.hotelId };
  const query: any = { ...bookingStore.routeParameters };

  const partner: any = route?.query?.partner_id;
  const origin: any = route?.query?.origin;

  if (partner) {
    query.partner_id = partner;
  }

  if (origin) {
    query.origin = origin;
  }

  navigateTo(localePath({ name, params, query }));
}

function handleRerender() {
  // Trigger a rerender of the NuxtPage component
  // It is mainly to refresh the room.vue component in order to trigger the
  // availableRoom query with the new identificationPlaintext header
  nuxtPageKey.value++;
}

watch(error, err => {
  if (!err) return;

  layoutStore.pauseSkeletons = true;

  event('funnel_error', {
    error_type: err.message,
  });

  if (Array.isArray(err?.graphQLErrors)) {
    const isCookieError = (err.graphQLErrors as GraphQLError[]).some(
      gqlError =>
        (gqlError.extensions?.code as string) === 'BOOKING_COOKIE_MISSING',
    );

    if (isCookieError) {
      onShouldRedirect();
    }
  }
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';

.Layout--context {
  margin-bottom: constants.$margin-02;
}

.Booking-header {
  margin-top: constants.$header-large;
}

.Layout--grey {
  background-color: colors.$neutral-100;
}

.Booking-body {
  min-height: calc(100vh - 96px - 98px);
  padding-top: constants.$padding-02;

  @media (mq.$from-medium) {
    flex-direction: row-reverse;
  }
}

.Booking-timeValidationBanner {
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-05;
  }
}

.Booking-mainContent {
  margin-bottom: constants.$margin-03;

  @media (mq.$from-medium) {
    margin-bottom: constants.$margin-04;
  }
}

.theme.rixos {
  .Layout-wrapper {
    background: colors.$white;

    .Booking-body {
      background: colors.$white;
    }
  }
}
</style>
